import React, { useEffect, useState, useRef, memo } from 'react';
import { useIntl } from 'react-intl';
import { buildFacet, buildNumericFacet } from '@coveo/headless/product-listing';
import { useCapUtils } from '../../cap/hooks/useCapUtils';
import { useUserContext } from '../../../aem-core-components/context/UserContext';
import { useCartState } from '../../../contexts/cart';
import { useFilterState } from '../../cap';
import { usePricing } from '../../../hooks/usePricing';
import useCheckLocationEmpty from '../../../hooks/useCheckLocationEmpty';
import useSearchUtils from '../hooks/useSearchUtils';
import useAnalytics from '../../../hooks/useAnalytics';
import SearchFiltersMulti from '../components/facets/filters/SearchFiltersMulti';
import Pagination from '../components/facets/pagination/Pagination';
import SortOrder from '../components/facets/sort';
import ProductListing from '../components/productListing/ProductListing';
import PlpNoResults from './PlpNoResults';
import NoAvailableResult from '../../global/atoms/noAvailableResult';
import ToggleSwitch from '../../global/atoms/ToggleSwitch/ToggleSwitch';
import { LoadingIndicator } from '../../../aem-core-components';
import {
    nameSortAsc,
    nameSortDesc,
    pagerController,
    productListingController,
    relevanceSortCriterion,
    sortController
} from '../controllers/plplistcontroller';
import { productListingEngine, updateContextBasedOnCompanyId } from '../Engine';
import useFavourites from '../../favorites/hooks/useFavorites';
import './plp.scss';
import {
    checkIsRequestQuote,
    isAtpCheckRequired,
    isNearByPcsRequired,
    isTier2Radius
} from '../../global/utils/commonUtils';
import { getProductListUrl } from '../utils/searchUtils';
import { isValidString, logError } from '../../global/utils/logger';
import { ENV_CONFIG } from '../../../constants/envConfig';
import { STORAGE_CONFIG } from '../../../constants/storageConfig';
import { VARIABLE_CONFIG } from '../../../constants/analyticsConstants/Variables';
import {
    SET_SHOW_NO_RESULTS_FOR_UNAVILABLE_ITEMS,
    SET_SHOW_UNAVAILABLE_ITEMS
} from '../../../aem-core-components/actions/constants';
import { plpDataLocators } from './dataLocators';
import { AUTHORITY_TYPE } from '../../global/constants';
import { useCheckAuthorityType } from '../../../hooks/useCheckUser';
import { useAnalyticsContext } from '../../../config/GoogleTagManagerEvents';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../../constants/analyticsConstants/Ecommerce';

const Plp = () => {
    const intl = useIntl();
    const [{ isProfileLoaded }] = useUserContext();
    const [{ cidPcList, isRatesLoading }] = useCartState();
    const { checkIfProductIsFavorite } = useFavourites();
    const [{ showUnavailableItems, showNoResultsForUnavailableItems }, filterDispatch] = useFilterState();
    const [{ initFacetsControllerList }] = useSearchUtils();
    const { getProductPrices } = usePricing();
    const [facetsList, setFacetsList] = useState([]);
    const [controllerConfig, setControllerConfig] = useState([]);
    const [hasL3Facets, setHasL3Facets] = useState();
    const [productList, setProductList] = useState(productListingController?.state);
    const [isProductsLoading, setIsProductsLoading] = useState(true);
    const { showToggleButton } = useCapUtils();
    const { isRentalDetailsAvailable } = useCheckLocationEmpty();
    const prevProductsRef = useRef([]);
    const authorityType = useCheckAuthorityType();
    const { sendEventsForUnavailableItem } = useAnalyticsContext();
    let breadcrumbActiveItem = ENV_CONFIG.BREADCRUMB_ACTIVE_LINK?.textContent || '';
    const [{ payloadEcommerceViewItemList, handleAvailabilityToggleEvents }] = useAnalytics();
    const { sendEventsForViewItemList } = useAnalyticsContext();
    const [isRateFetched, setIsRateFetched] = useState(false);

    const onProductListingControllerStateChange = () => {
        const { state } = productListingController;
        const { responseId, error } = state;
        setProductList(state);
        if (error || responseId) {
            setIsProductsLoading(false);
        } else {
            setIsProductsLoading(true);
        }
    };
    useEffect(() => {
        const overridePc = JSON.parse(sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.OVERRIDEPC) || '{}');

        filterDispatch({ type: SET_SHOW_UNAVAILABLE_ITEMS, showUnavailableItems: true });
        if (!overridePc?.pc) {
            updateContextBasedOnCompanyId(
                [],
                [
                    {
                        key: 'nearbypcs',
                        val: isAtpCheckRequired()
                    }
                ],
                isNearByPcsRequired(false)
            );
        }
        productListingController.subscribe(onProductListingControllerStateChange);
    }, []);

    useEffect(() => {
        prevProductsRef.current = [];
    }, [cidPcList]);

    useEffect(() => {
        if (isProfileLoaded ) {
            if (checkEmptyProducts() && productList?.responseId) {
                logError('No items are available', false, 'checkEmptyProducts', [
                    cidPcList,
                    window?.location?.href,
                    productList?.responseId,
                    productList?.error,
                    productList?.products
                ]);
            }
        }
    }, [productList?.responseId, isProfileLoaded]);
    
    useEffect(() => {
        if (isProfileLoaded) {
            if (isRentalDetailsAvailable() && cidPcList?.length > 0) {
                const level = document.querySelector("meta[name='categoryLevel']")?.content;
                if (level && level == 4) {
                    if (hasL3Facets) {
                        getPriceForProducts();
                    } else {
                        if (hasL3Facets === false) {
                            //the below code redirects to l2 page
                            rediectToL2fromL3();
                        }
                    }
                } else {
                    getPriceForProducts();
                }
            }
        }
    }, [productList?.responseId, cidPcList, isProfileLoaded, hasL3Facets]);

    useEffect(() => {
        getInitfacet();
    }, [productListingController]);

    useEffect(() => {
        if (!isProductsLoading && !checkEmptyProducts()) {
            if (!isRentalDetailsAvailable() || (!isRatesLoading && isRateFetched)) {
                sendEventsForViewItemList(
                    EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_VIEW_ITEM_LIST,
                    payloadEcommerceViewItemList(productList?.products)
                );
            }
        }
    }, [isRatesLoading, isRateFetched, isProductsLoading]);

    const checkEmptyProducts = () => {
        if (productList?.products?.length > 0) {
            return false;
        } else {
            return true;
        }
    };

    useEffect(() => {
        if (!isProductsLoading) {
            if (checkEmptyProducts() && showUnavailableItems) {
                filterDispatch({
                    type: SET_SHOW_NO_RESULTS_FOR_UNAVILABLE_ITEMS,
                    showNoResultsForUnavailableItems: true
                });
            }
            //TODO add P2P condition check
            if (authorityType === AUTHORITY_TYPE.P2P) {
                const productSkus = productList?.products?.map(product => product?.additionalFields?.ec_skus)?.flat();
                if (productSkus?.length > 0) {
                    checkIfProductIsFavorite({
                        sku: productSkus,
                        pageSize: productSkus?.length
                    });
                }
            }
        }
    }, [productList?.products?.length, showUnavailableItems, isProductsLoading]);

    useEffect(() => {
        if (!showUnavailableItems && !isProductsLoading) {
            sendEventsForUnavailableItem(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_INVENTORY_ALERT_VIEWED,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_UNAVAILABLE_FROM_PC
            );
        }
    }, [showUnavailableItems, isProductsLoading]);

    const shouldFetchRates = () => {
        if (hasL3Facets) {
            return productList?.products;
        } else {
            const deltaProducts = productList?.products?.filter(item => {
                const { ec_skus } = item?.additionalFields;
                if (prevProductsRef?.current?.indexOf(ec_skus[0]) === -1) {
                    return true;
                }

                return false;
            });

            return deltaProducts;
        }
    };

    useEffect(() => {
        if (!isProductsLoading && ENV_CONFIG.POPULAR_CATEGORY_CONTAINER) {
            if (checkEmptyProducts()) {
                ENV_CONFIG.POPULAR_CATEGORY_CONTAINER.style.display = 'block';
                if (ENV_CONFIG.PLP_SEARCH_PAGE_CONTAINER) ENV_CONFIG.PLP_SEARCH_PAGE_CONTAINER.style.minHeight = 'auto';
                if (ENV_CONFIG.SRPSeasonalCarousel) {
                    $('.seasonal__carousel')?.slick('refresh');
                }
            } else {
                ENV_CONFIG.POPULAR_CATEGORY_CONTAINER.style.display = 'none';
            }
        }
    }, [productList?.products, isProductsLoading]);

    const handleToggleChange = checked => {
        filterDispatch({ type: SET_SHOW_UNAVAILABLE_ITEMS, showUnavailableItems: checked });
        updateContextBasedOnCompanyId(
            [],
            [
                {
                    key: 'nearbypcs',
                    val: isAtpCheckRequired()
                }
            ],
            !checked
        );
        productListingController.refresh();
        handleAvailabilityToggleEvents(checked);
        setIsRateFetched(false);
    };

    const getPriceForProducts = async () => {
        const deltaProducts = shouldFetchRates();
        if (deltaProducts?.length > 0) {
            getProductPrices(deltaProducts, VARIABLE_CONFIG.PAGE_TYPE.PLP);
            prevProductsRef.current = productList?.products.map(item => item?.additionalFields.ec_skus[0]);
        }
        setIsRateFetched(true);
    };

    const getInitfacet = async () => {
        const { facetsControllerList, controllerConfig } = await initFacetsControllerList(
            productListingEngine,
            buildFacet,
            buildNumericFacet,
            getProductListUrl(),
            productListingController
        );
        setFacetsList(facetsControllerList);
        //TODO- once subcategory is a dynamic facet, make sure if comes at top ,remove from config
        setControllerConfig(controllerConfig);
    };

    const presetL3Facet = controller => {
        try {
            const level = document.querySelector("meta[name='categoryLevel']")?.content;
            if (level == 4) {
                const category = document.querySelector("meta[name='categoryTitle']")?.content;
                const selFilter = controller?.state?.values?.find(item => item.value === category);
                if (selFilter) {
                    setHasL3Facets(true);
                    controller.toggleSelect(selFilter);
                    return [category];
                }
            }
            return [];
        } catch (e) {
            logError('error while setting l3 facet', false, 'presetL3Facet', [controller]);
            return [];
        }
    };

    const rediectToL2fromL3 = () => {
        try {
            let linkArr = window.location.href.split('/');
            if (linkArr[linkArr.length - 1] === '') {
                linkArr.pop();
            }
            linkArr.pop();
            linkArr = linkArr.join('/');
            let url = `${linkArr}/`;
            window.location.href = url;
        } catch (e) {
            logError('exception while redirect', false, 'rediectToL2fromL3');
        }
    };

    const showUnavailableClickHandler = event => {
        event?.stopPropagation();
        handleToggleChange(false);
    };

    const showNoResults = checkEmptyProducts() || showNoResultsForUnavailableItems;

    const renderPlpListing = () => {
        return (
            <>
                <div className="col-lg-3">
                    <div className="plp__filter plp__left-filter">
                        <div className="plp__left-filterbutton">
                            <SearchFiltersMulti
                                title={'Filter'}
                                filterTitleClass={'plp__filter-title'}
                                filterBodyClasess={'asdf'}
                                facetControllerList={facetsList}
                                controllerConfig={controllerConfig}
                                triggerEvents={true}
                                presetFacet={presetL3Facet}
                                setHasFacets={setHasL3Facets}
                            />
                        </div>
                        <div className="plp__left-dropdown">
                            <SortOrder
                                controller={sortController}
                                nameSortAsc={nameSortAsc}
                                nameSortDesc={nameSortDesc}
                                relevanceSortCriterion={relevanceSortCriterion}
                            />
                        </div>
                    </div>
                </div>
                <div className="col-lg-9" id="searchresultsection">
                    <div className="plp__right-filter">
                        <div className="plp__right-filter-buttonselect">
                            <div className="plp__right-filter-mobile-filter">
                                <SortOrder
                                    controller={sortController}
                                    nameSortAsc={nameSortAsc}
                                    nameSortDesc={nameSortDesc}
                                    relevanceSortCriterion={relevanceSortCriterion}
                                />
                            </div>
                            {showToggleButton() && (
                                <div className="plp__right-filter-toggle">
                                    <ToggleSwitch
                                        id={plpDataLocators.plp_toggle_switch}
                                        checked={showUnavailableItems}
                                        onChange={handleToggleChange}
                                        name={intl.formatMessage({ id: 'hide-unavailable-items' })}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    {checkEmptyProducts() ? (
                        <NoAvailableResult
                            searchQuery={breadcrumbActiveItem}
                            onShowUnavailableClick={showUnavailableClickHandler}
                            dataTestid={plpDataLocators.plp_show_unavailable_products}
                            count={productList?.products?.length}
                        />
                    ) : (
                        <>
                            <div>
                                <ProductListing
                                    productList={productList}
                                    totalCount={productListingEngine?.state?.pagination?.totalCountFiltered}
                                />
                            </div>
                            <div>
                                <Pagination controller={pagerController} />
                            </div>
                        </>
                    )}
                </div>
            </>
        );
    };
    const renderPlp = () => {
        return (
            <>
                {showNoResults && (
                    <div className="col-lg-12">
                        <PlpNoResults />
                    </div>
                )}
                {renderPlpListing()}
            </>
        );
    };

    return (
        <section className={`block block--plp ${showNoResults ? 'plp-unavailable' : ''}`}>
            <div className="container">
                <div className="row">
                    {isProductsLoading ? (
                        <div className={'plp__loading-indicator'}>
                            <LoadingIndicator />
                        </div>
                    ) : (
                        renderPlp()
                    )}
                </div>
            </div>
        </section>
    );
};

export default memo(Plp);
